import React, { useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { PersonDeleteFill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { usePublishAssignmentMutation } from '@/modules/corporate/modules/assignment/views/AssignmentCreator/graphql/mutations/generated/publishAssignment';
import { useRemoveUsersFromStaffMutation } from '@/modules/corporate/modules/learners/graphql/mutations/generated/removeUsersFromStaff';
import useReportTableActions from '@/modules/learner/hooks/useReportTableActions';
import { LearnerProfileDetailsFragment } from '@/modules/learner/views/LearnerProfile/graphql/fragments/generated/LearnerProfileDetails';
import { useLearnerProfileDetailsQuery } from '@/modules/learner/views/LearnerProfile/graphql/queries/generated/learnerProfileDetails';
import routes from '@/pages/corporate/routes';
import { AssignmentDueDateEnum, Maybe, PublishAssignmentInput } from '@/types/schema';
import { getErrorCode } from '@/utils/api/response';
import { useDebug } from '@/utils/debug';

import { useLearnerProfileupdateUserMutation } from '../../graphql/mutations/generated/learnerProfileUpdateuser';
import {
  LearnerProfileActionTypes,
  LearnerProfileModalType,
  OnLearnerProfileAction,
} from './actions';

interface Props {
  learnerId: string;
  isMGSAProfile: boolean;
}

const OWNER_REMOVAL_ERROR_CODE = 'OWNER_REMOVAL_NOT_ALLOWED';

const useLearnerProfile = ({ learnerId, isMGSAProfile }: Props) => {
  const [{ fetching, error, data, stale }] = useLearnerProfileDetailsQuery({
    variables: { learnerId, fetchAPIClientId: isMGSAProfile },
  });
  const { onCopyRoute } = useCopyToClipboard();
  const [{ fetching: isUpdatingLearnerProfile }, updateUser] =
    useLearnerProfileupdateUserMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [inViewModal, setInViewModal] = useState<LearnerProfileModalType | null>(null);
  const [, executeRemoveUsersFromStaff] = useRemoveUsersFromStaffMutation();
  const [{ fetching: isPublishingAssignment }, publishAssignment] = usePublishAssignmentMutation();

  const { t } = useTranslation();

  const { onAction: onReportTableAction } = useReportTableActions();

  const onAction: OnLearnerProfileAction = useEvent(async (action) => {
    switch (action.type) {
      case LearnerProfileActionTypes.ShareLearnerProfile: {
        onCopyRoute(routes.AdminLearnerProfile(learnerId));
        break;
      }
      case LearnerProfileActionTypes.SetInViewModal: {
        setInViewModal(action.payload.modalType);
        break;
      }
      case LearnerProfileActionTypes.HideInViewModal: {
        setInViewModal(null);
        break;
      }
      case LearnerProfileActionTypes.RemoveStaff: {
        const result = await executeRemoveUsersFromStaff({
          input: { userIds: [action.payload.learnerId] },
        });
        if (result?.data?.removeUsersFromStaff.success) {
          setInViewModal(null);
          showSnackbar({
            message: (
              <Trans
                as="span"
                color="text-text-white"
                i18nKey="Admin:LearnerAndGroups.RemoveStaffSuccess"
                values={{ name: action.payload.learnerName }}
              />
            ),
            startIcon: <PersonDeleteFill className="h-20 w-20" fillColorToken="icon-white" />,
          });
        } else {
          if (getErrorCode(result.error) === OWNER_REMOVAL_ERROR_CODE) {
            showErrorSnackbar({ message: t('Admin:LearnerAndGroups.CanNotDeleteOwner') });
          } else {
            showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
          }
        }

        break;
      }
      case LearnerProfileActionTypes.UpdateProfile: {
        return updateUser({ input: action.payload.updatedValues, learnerId }).then((response) => {
          if (response.error) {
            showDefaultErrorSnackbar();
          } else {
            showSnackbar({
              message: action.payload.snackbarSuccessMessage ?? t('LearnerProfile:ProfileUpdated'),
              startIcon: action.payload.snackbarStartIcon,
            });
          }
          setInViewModal(null);
        });
      }
      case LearnerProfileActionTypes.Assignment: {
        const { userId, assignerId, items, dueDate, onError, onSuccess } = action.payload;
        const data: PublishAssignmentInput = {
          assignerId,
          contentIds: items.selectedContentIds,
          dueDateConfig: {
            dueDateType: dueDate ? AssignmentDueDateEnum.Fixed : AssignmentDueDateEnum.NoDueDate,
            dueDate,
          },
          userIds: [userId],
          collectionIds: items.selectedCollectionIds,
          userGroupIds: [],
        };

        return publishAssignment({ data }).then((response) => {
          if (!response || response.error) {
            showDefaultErrorSnackbar();
            onError();
          } else {
            onSuccess(response.data?.publishAssignment?.assignment?.id!);
          }
        });
      }
      default: {
        onReportTableAction(action);
        break;
      }
    }
  });

  const debug = useDebug('UserProfile');
  if (debug) {
    console.log('useLearnerProfile', {
      fetching,
      stale,
      data,
    });
  }

  return {
    // since we are fetching workspace in same query, we need to check for stale data
    fetching: fetching || (stale && !data?.user),
    isPublishingAssignment,
    error,
    learner: data?.user as Maybe<LearnerProfileDetailsFragment>,
    onAction,
    inViewModal,
    isUpdatingLearnerProfile,
    userPublicAPIClientId: data?.currentWorkspace?.configs?.authConfig.userPublicAPIClientId,
  };
};

export default useLearnerProfile;
